import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import StatusBadge from "./StatusBadge";
import { Clock, ArrowRight, User, Layers } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${Math.round(diff)} san`;
  if (diff < 3600) return `${Math.round(diff / 60)} dəq`;
  if (diff < 86400) return `${Math.round(diff / 3600)} saat`;
  return `${Math.round(diff / 86400)} gün`;
}

const ROLE_COLORS = {
  consulting_tech_office: "#1D4ED8",
  consulting_inspector: "#10B981",
  consulting_topographer: "#F59E0B",
  consulting_material_engineer: "#7C3AED",
  consulting_estimate_engineer: "#FF5722",
  consulting_team_lead: "#DC2626",
};

export default function ActiveInspectionCard({ inspection, stage }) {
  const { t } = useI18n();
  const idx = inspection.current_step_index || 0;
  const workflow = inspection.workflow || [];
  const isFinal = inspection.status === "approved" || inspection.status === "rejected";
  const isAwaitingTL = inspection.status === "awaiting_team_lead";

  // Find the step where the action is currently waiting
  let activeStep = null;
  let activeIdx = idx;
  if (isAwaitingTL) {
    activeIdx = workflow.findIndex((s) => s.role === "consulting_team_lead");
    activeStep = workflow[activeIdx];
  } else if (idx < workflow.length) {
    activeStep = workflow[idx];
  }

  // Last completed step (for "received from")
  const lastDoneIdx = activeIdx > 0 ? activeIdx - 1 : -1;
  const lastDone = lastDoneIdx >= 0 ? workflow[lastDoneIdx] : null;

  const accent = activeStep ? ROLE_COLORS[activeStep.role] || "#004D99" : "#10B981";
  const completedCount = workflow.filter(
    (s) => s.status === "ok" || s.status === "not_ok"
  ).length;
  const progress = (completedCount / workflow.length) * 100;

  return (
    <Link
      to={`/inspections/${inspection.id}`}
      className="block bg-white border-2 border-slate-200 rounded-lg p-5 hover:border-[#004D99] hover:shadow-md transition-all"
      data-testid={`active-insp-${inspection.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="mono text-[10px] text-slate-400">
              #{inspection.id.slice(0, 8)}
            </span>
            <StatusBadge status={inspection.status} />
          </div>
          <h3 className="font-heading font-bold text-base flex items-center gap-1.5">
            <Layers className="w-4 h-4" />
            {stage?.title || (stage ? t(stage.stage_type) : "—")}
          </h3>
        </div>
        <ArrowRight className="w-4 h-4 text-slate-300 shrink-0" />
      </div>

      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex justify-between mono text-[10px] text-slate-500 mb-1">
          <span>İRƏLILƏYIŞ</span>
          <span>
            {completedCount}/{workflow.length} mərhələ
          </span>
        </div>
        <div className="h-1.5 bg-slate-100 rounded">
          <div
            className="h-full rounded transition-all"
            style={{ width: `${progress}%`, backgroundColor: accent }}
          />
        </div>
      </div>

      {/* Currently waiting on */}
      {activeStep && !isFinal ? (
        <div
          className="rounded-lg p-3 border-2 border-dashed flex items-start gap-3"
          style={{ borderColor: accent, backgroundColor: `${accent}10` }}
        >
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0"
            style={{ backgroundColor: accent }}
          >
            {activeStep.assignee?.full_name?.[0]?.toUpperCase() || (
              <User className="w-5 h-5" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] mono uppercase font-bold" style={{ color: accent }}>
              HAZIRDA KIMDƏDIR
            </div>
            <div className="text-sm font-bold truncate">
              {activeStep.assignee?.full_name || "Təyinat olunmayıb"}
            </div>
            <div className="text-xs text-slate-600 truncate">
              {t(`role_label.${activeStep.role}`)}
              {activeStep.assignee?.email && ` · ${activeStep.assignee.email}`}
            </div>
            {lastDone && (
              <div className="flex items-center gap-1 mt-1.5 text-[10px] mono text-slate-500">
                <Clock className="w-3 h-3" />
                {timeAgo(lastDone.completed_at)} əvvəl{" "}
                {t(`role_label.${lastDone.role}`)}-dən gəldi
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="rounded-lg p-3 bg-slate-50 border border-slate-200 text-sm">
          <div className="font-semibold">
            {inspection.status === "approved"
              ? "✓ Tamamlandı (Təsdiqləndi)"
              : inspection.status === "rejected"
                ? "✗ Rədd edildi"
                : "Tamamlandı"}
          </div>
        </div>
      )}
    </Link>
  );
}
